import React, { useState } from 'react'
import { ArrowLeft, Check, Image, Calendar, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useApprovedGalleries } from '@/hooks/useSocialMedia'
import { format } from 'date-fns'

interface GalleryImageSelectorProps {
  onImagesSelected: (imageUrls: string[]) => void
  onClose: () => void
  maxImages?: number
}

export default function GalleryImageSelector({ 
  onImagesSelected, 
  onClose,
  maxImages = 10 
}: GalleryImageSelectorProps) {
  const [selectedGallery, setSelectedGallery] = useState<any>(null)
  const [selectedImages, setSelectedImages] = useState<string[]>([])
  const { data: galleries = [], isLoading } = useApprovedGalleries()

  const handleGallerySelect = (gallery: any) => {
    setSelectedGallery(gallery)
    setSelectedImages([])
  }

  const handleImageToggle = (imageUrl: string) => {
    setSelectedImages(prev => {
      if (prev.includes(imageUrl)) {
        return prev.filter(url => url !== imageUrl)
      }
      if (prev.length >= maxImages) {
        return prev
      }
      return [...prev, imageUrl]
    })
  }

  const handleConfirmSelection = () => {
    onImagesSelected(selectedImages)
    onClose()
  }

  const getRegionBadgeColor = (region: string) => {
    const colors: Record<string, string> = {
      'HQ': 'bg-purple-500',
      'north_york': 'bg-blue-500',
      'london': 'bg-green-500',
      'toronto': 'bg-red-500',
      'hamilton': 'bg-orange-500',
      'mississauga': 'bg-pink-500',
      'brampton': 'bg-yellow-500',
      'scarborough': 'bg-indigo-500',
      'general': 'bg-gray-500'
    }
    return colors[region] || 'bg-gray-500'
  }

  const formatRegionName = (region: string) => {
    return region.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-32 h-32 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </Card>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b">
          <div className="flex items-center gap-4">
            {selectedGallery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedGallery(null)
                  setSelectedImages([])
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Galleries
              </Button>
            )}
            <CardTitle>
              {selectedGallery ? selectedGallery.title : 'Select Gallery'}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {selectedImages.length > 0 && (
              <Badge variant="secondary">
                {selectedImages.length} / {maxImages} selected
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardHeader>
        
        <ScrollArea className="h-[calc(90vh-8rem)]">
          <CardContent className="p-6">
            {!selectedGallery ? (
              // Gallery List View
              <div className="space-y-4">
                <div className="grid gap-4">
                  {galleries.map((gallery) => (
                    <Card 
                      key={gallery.id}
                      className="cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => handleGallerySelect(gallery)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-1">{gallery.title}</h3>
                            {gallery.description && (
                              <p className="text-sm text-muted-foreground mb-3">
                                {gallery.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                <Badge 
                                  variant="secondary"
                                  className={`${getRegionBadgeColor(gallery.franchise_region)} text-white`}
                                >
                                  {formatRegionName(gallery.franchise_region)}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-1">
                                <Image className="h-4 w-4" />
                                <span>{gallery.gallery_photos?.length || 0} photos</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                <span>{format(new Date(gallery.created_on), 'MMM d, yyyy')}</span>
                              </div>
                            </div>
                          </div>
                          {gallery.gallery_photos && gallery.gallery_photos.length > 0 && (
                            <div className="ml-4 grid grid-cols-3 gap-1 w-32">
                              {gallery.gallery_photos.slice(0, 3).map((photo, idx) => (
                                <img
                                  key={idx}
                                  src={photo.image_url}
                                  alt=""
                                  className="w-10 h-10 object-cover rounded"
                                />
                              ))}
                              {gallery.gallery_photos.length > 3 && (
                                <div className="w-10 h-10 bg-muted rounded flex items-center justify-center text-xs">
                                  +{gallery.gallery_photos.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              // Photo Grid View
              <div className="space-y-4">
                {selectedGallery.description && (
                  <p className="text-muted-foreground">{selectedGallery.description}</p>
                )}
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {selectedGallery.gallery_photos?.map((photo: any) => {
                    const isSelected = selectedImages.includes(photo.image_url)
                    return (
                      <div
                        key={photo.id}
                        className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                          isSelected ? 'border-primary' : 'border-transparent'
                        }`}
                        onClick={() => handleImageToggle(photo.image_url)}
                      >
                        <img
                          src={photo.image_url}
                          alt={photo.caption || ''}
                          className="w-full h-40 object-cover"
                        />
                        {photo.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2 text-xs">
                            {photo.caption}
                          </div>
                        )}
                        <div className={`absolute inset-0 transition-opacity ${
                          isSelected ? 'bg-primary/20' : 'bg-black/0 group-hover:bg-black/20'
                        }`}>
                          {isSelected && (
                            <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                              <Check className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
                
                {selectedImages.length > 0 && (
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setSelectedImages([])}
                    >
                      Clear Selection
                    </Button>
                    <Button onClick={handleConfirmSelection}>
                      Add {selectedImages.length} Image{selectedImages.length !== 1 ? 's' : ''}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </ScrollArea>
      </Card>
    </div>
  )
}